import React from "react";

import { getDuration, getDurationString } from "app/lib/date";
import { shortCommit } from "app/lib/commits";
import { track } from "app/lib/segmentAnalytics";
import BuildMessage from "app/components/build/Message";
import BuildsStore, { Build } from "app/stores/BuildsStore";
import BuildState from "app/components/icons/BuildState";
import Duration from "app/components/shared/Duration";
import FriendlyTime from "app/components/shared/FriendlyTime";
import Icon from "app/components/shared/Icon";
import UserAvatar from "app/components/shared/UserAvatar";
import {
  getDurationPrefix,
  getDurationSuffix,
  getDurationValues,
} from "app/components/build/DurationNext";

import Steps from "./Header/pipeline/Steps";

function BuildMeta({ build }: { build: Build }) {
  const sha = shortCommit(build.commitId, build.commitShortLength);
  const seperator = <span>&middot;</span>;

  return (
    <div className="build-row-meta flex flex-wrap gapx1.5 charcoal-600 items-center text-xs leading-5">
      <span className="flex items-center gap1">
        <UserAvatar
          // @ts-expect-error - TS2339 - Property 'authorAvatar' does not exist on type 'Build'.
          user={{ name: build.authorName, avatar: { url: build.authorAvatar } }}
          style={{ width: 20, height: 20 }}
          suppressAltText={true}
        />
        {build.authorName}
      </span>

      {seperator}

      <span className="flex items-center gap0.5">
        <a
          data-turbo="true"
          // @ts-expect-error - TS2339 - Property 'branchPath' does not exist on type 'Build'.
          href={build.branchPath}
          className="flex color-inherit hover-color-inherit flex items-center"
        >
          <Icon
            icon="custom/outline/14x/branch"
            style={{ height: 14, width: 14 }}
          />
          {/* @ts-expect-error - TS2339 - Property 'branchName' does not exist on type 'Build'. */}
          {build.branchName}
        </a>

        <Icon
          icon="custom/outline/20x/slash"
          style={{ height: 14, width: 14 }}
        />

        <a
          // @ts-expect-error - TS2339 - Property 'commitUrl' does not exist on type 'Build'.
          href={build.commitUrl}
          target="_blank"
          className="monospace flex color-inherit hover-color-inherit flex items-center"
          rel="noreferrer"
          style={{ fontSize: 12 }}
        >
          <Icon
            // @ts-expect-error - TS2339 - Property 'project' does not exist on type 'Build'.
            icon={build.project.provider.frontendIcon}
            className="mr1"
            style={{ height: 14, width: 14 }}
          />
          {sha}
        </a>
      </span>

      {seperator}

      <span>
        Created <FriendlyTime value={build.createdAt} />
      </span>
    </div>
  );
}

function getTooltip(build: Build) {
  const prefix = getDurationPrefix(build) || "";
  const suffix = getDurationSuffix(build) || "";

  const durationValues = getDurationValues(build);
  const durationString = durationValues
    ? // @ts-expect-error - TS2345 - Argument of type 'Duration' is not assignable to parameter of type 'number'.
      getDurationString(getDuration(durationValues.from, durationValues.to))
    : "";

  return `${prefix} ${durationString} ${suffix}`.trim();
}

const BuildRow = ({ build, store }: { build: Build; store: BuildsStore }) => {
  const durationValues = getDurationValues(build);
  const tooltip = getTooltip(build);

  const buildState =
    build.state === "started" ? "RUNNING" : build.state.toUpperCase();

  const [showSteps, setShowSteps] = React.useState(false);

  function toggleSteps(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.currentTarget.blur();

    track("Builds List Toggle Steps Clicked", {
      action: showSteps ? "Hide" : "Show",
      text: showSteps
        ? `Hide steps for #${build.number}`
        : `Show steps for #${build.number}`,
      source: "Build Row",
    });

    setShowSteps(!showSteps);
  }

  return (
    <div className="build-row border-bottom border-gray antialiased">
      <div className="build-row-header gapx2.5 gapy0.5 py2 px3">
        <div
          className="build-row-status self-center flex justify-center cursor-help"
          title={tooltip}
        >
          <BuildState.Regular
            state={buildState}
            blockedState={
              // @ts-expect-error - TS2339 - Property 'blockedState' does not exist on type 'Build'. | TS2339 - Property 'blockedState' does not exist on type 'Build'.
              build.blockedState && build.blockedState.toUpperCase()
            }
            className="block"
          />
        </div>
        <div
          className="build-row-duration charcoal-600 text-center nowrap cursor-help -mx-2 tracking-tight text-xs leading-5"
          title={tooltip}
        >
          {durationValues ? (
            <Duration.Micro {...durationValues} />
          ) : (
            <span className="gray-800">—</span>
          )}
        </div>

        <a
          className="build-row-message  group hover:no-underline self-center inline-flex min-width-0"
          href={build.path}
        >
          <span className="charcoal-900 bold truncate min-w-0 group-hover:underline">
            <BuildMessage message={build.message} />
          </span>

          <span className="gray-800 ml1 group-hover:no-underline">
            #{build.number}
          </span>
        </a>

        <BuildMeta build={build} />

        {/* @ts-expect-error - TS2339 - Property 'jobs' does not exist on type 'Build'. */}
        {build.jobs && (
          <div className="build-row-toggle-steps self-center -mx0.5">
            <button
              onClick={toggleSteps}
              className="unstyled-button charcoal-300 hover-charcoal-800 p1"
              aria-label={showSteps ? "Hide steps" : "Show steps"}
            >
              <span className="hide">
                {showSteps
                  ? `Hide steps for #${build.number}`
                  : `Show steps for #${build.number}`}
              </span>

              <Icon
                icon="heroicons/20/solid/chevron-up-down"
                style={{ height: 20, width: 20 }}
              />
            </button>
          </div>
        )}
      </div>

      {showSteps && (
        <div
          className="build-row-steps flex flex-wrap flex-auto gap1 border-top border-gray-400 bg-slate-100 shadow-inset px3 py2"
          style={{ paddingLeft: 49 }}
        >
          <Steps build={build} store={store} small={true} />
        </div>
      )}
    </div>
  );
};

export default BuildRow;
